// src/app/core/interceptors/nfc-auth.interceptor.ts
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/api/auth.service';
import { NfcService } from '../services/api/nfc.service';

/**
 * 🔐 Interceptor funcional que valida NFC antes de cada petición HTTP
 * Solo aplica para usuarios con roles que requieren NFC
 */
export const nfcAuthInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const nfcService = inject(NfcService);
    const router = inject(Router);

    // URLs excluidas de validación NFC
    const EXCLUDED_URLS = [
        '/auth/login',
        '/auth/nfc-login',
        '/auth/refresh',
        '/auth/register',
        'apitoken.arco.com.py' // Servicio NFC local
    ];

    // Si la URL está excluida, continúa sin validar
    if (EXCLUDED_URLS.some((url) => req.url.includes(url))) {
        return next(req);
    }

    // Si el usuario no requiere NFC, continúa normalmente
    if (!authService.requiresNfc()) {
        return next(req);
    }

    return nfcService.verifyCurrentCard().pipe(
        switchMap((isValid) => {
            if (!isValid) {
                if (!nfcService.hasStoredNfcData()) {
                    authService.logout();
                    router.navigate(['/auth/login'], {
                        queryParams: { error: 'nfc_disconnected', message: 'Credencial NFC no detectado' }
                    });
                    return throwError(() => new Error('Credencial NFC no detectado'));
                }
                return nfcService.verifyCurrentCard().pipe(
                    switchMap((retryValid) => {
                        if (!retryValid) {
                            authService.logout();
                            router.navigate(['/auth/login'], {
                                queryParams: { error: 'nfc_disconnected', message: 'Credencial NFC desconectado durante operación' }
                            });
                            return throwError(() => new Error('Credencial NFC no detectado'));
                        }
                        return next(req);
                    })
                );
            }
            return next(req);
        }),
        catchError((error) => {
            if (error.status === 401) {
                authService.logout();
            }
            return throwError(() => error);
        })
    );
};
